import { updateFlareNodeAttrs } from '@common/flare/prosemirror/transform/node';
import { createTable, DimensionOptions, TableNodeTypes } from '@common/prosemirror/commands/table.command';
import { closestBlockAncestor, findNode, FoundNodeInfo } from '@common/prosemirror/model/node-find';
import { getTrackedChangesState, TrackedChangesTrackedMetaKey } from '@common/prosemirror/plugins/tracked-changes.plugin';
import { ChangeType } from '@common/prosemirror/tracked-changes/change-type.enum';
import { copySliceWithChanges } from '@common/prosemirror/tracked-changes/copy';
import { updateNodeAttrs } from '@common/prosemirror/transform/node';
import { Attrs, Fragment, Node, NodeType, ResolvedPos, Slice } from 'prosemirror-model';
import { EditorState, NodeSelection, Selection, Transaction } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';

/**
 * Gets the start position of a node.
 * Takes advantage of ProseMirror reusing nodes across doc states because they are immutable.
 * Searches the doc for the newBlock by doing a reference comparison between the newBlock and the nodes in the doc.
 * This gives us the exact position of the node after the "fuzzy" insertion by `Transaction.replaceSelectionWith`.
 * @param tr
 * @param newBlock
 * @returns The start position of the new block node.
 */
function getNodeStart(tr: Transaction, newBlock: Node): number {
  return findNode(tr.doc, node => node === newBlock)?.pos;
}

/**
 * Gets the end position of a node.
 * Takes advantage of ProseMirror reusing nodes across doc states because they are immutable.
 * Searches the doc for the newBlock by doing a reference comparison between the newBlock and the nodes in the doc.
 * This gives us the exact position of the node after the "fuzzy" insertion by `Transaction.replaceSelectionWith`.
 * @param tr
 * @param newBlock
 * @returns The start position of the new block node.
 */
function getNodeEnd(tr: Transaction, newBlock: Node): number {
  const nodeStart = getNodeStart(tr, newBlock);
  return typeof nodeStart === 'number' ? nodeStart + newBlock.nodeSize : undefined;
}

/**
 * Returns the closest block at a pos in the selection.
 * If the selection is a NodeSelection and a block then the selected node is the closest block.
 * Otherwise $pos is traversed upwards until a block node is found.
 * @param selection the selection.
 * @param $pos The position which should be selection.$to or selection.$from.
 * @returns the closest block node information.
 */
function getClosestBlock(selection: Selection, $pos: ResolvedPos): FoundNodeInfo {
  // If the selection is a node selection, then the closest block is the selected node
  if (selection instanceof NodeSelection && selection.node.isBlock) {
    return {
      node: selection.node,
      pos: selection.from
    }
  }
  // Otherwise, find the closest block ancestor of the selection's from position
  return closestBlockAncestor($pos);
}

/**
 * Returns whether a slice is empty for tracked changes.
 * A slice is considered empty if it has no leaf nodes. e.g. no text or images.
 * @param slice The slice to check.
 * @returns Returns true if the slice has no leaf nodes, false otherwise.
 */
function isEmptySlice(slice: Slice): boolean {
  if (slice.size === 0) {
    return true;
  }

  let isEmpty = true;

  // If the slice has no leaf nodes then consider it empty
  slice.content.descendants(node => {
    if (node.isLeaf) {
      isEmpty = false;
      return false;
    }
  });

  return isEmpty;
}

/**
 * Returns whether a slice contains only a passthrough block.
 * @param slice The slice to check.
 * @returns True if the slice contains only an empty passthrough block, false otherwise.
 */
function isPassthroughSlice(slice: Slice): boolean {
  return slice.content.size === 2 && slice.content.firstChild?.type.spec.passthroughBlock;
}

export type InsertBlockNodeCommand = (state: EditorState, dispatch?: ProsemirrorDispatcher, view?: EditorView, attrs?: Attrs, options?: DimensionOptions) => boolean;

export function insertBlockNode(nodeType: NodeType, tableNodeTypes?: TableNodeTypes): InsertBlockNodeCommand {
  return function (state: EditorState, dispatch?: ProsemirrorDispatcher, view?: EditorView, attrs?: Attrs, options?: DimensionOptions): boolean {
    // Node must be a block node
    if (!nodeType.isBlock) {
      return false;
    }

    if (dispatch) {
      const trackedChangesState = getTrackedChangesState(state);
      // Find the position after the end of the original to insert
      const selection: Selection = state.selection;
      const tr: Transaction = state.tr;

      const node = selection instanceof NodeSelection && selection.node.type === nodeType ? selection.node : undefined;
      // If the same node type is selected, then update the attributes instead of replacing the node
      if (node) {
        updateFlareNodeAttrs(tr, node, selection.from, attrs, trackedChangesState);
        tr.setSelection(NodeSelection.create(tr.doc, selection.from));
        // Insert the block
      } else {
        // Create the new block to insert
        let newBlockNode: Node;
        if (nodeType === state.schema.nodes.table && tableNodeTypes) {
          newBlockNode = createTable(nodeType, tableNodeTypes.tbodyType, tableNodeTypes.rowType, tableNodeTypes.cellType, tableNodeTypes.colGroupType, tableNodeTypes.columnType, options.width, options.height, attrs);
        } else {
          newBlockNode = nodeType.create(attrs);
        }

        // Insert the new block. Do not inherit marks as that could make a new copy of newBlockNode causing getNodeStart/getNodeEnd to fail because the node reference no longer matches
        tr.replaceSelectionWith(newBlockNode, false);

        // Grab the position of the new block
        const newBlockStart = getNodeStart(tr, newBlockNode);

        // If the new block is not an atom, then set the selection to the start of the block's content
        if (!nodeType.isAtom) {
          if (typeof newBlockStart === 'number') {
            tr.setSelection(Selection.near(tr.doc.resolve(newBlockStart), 1));
          }
        }

        if (trackedChangesState?.enabled) {
          // Build the tracked changes after the new block is inserted so that we can use its position to check its parent for a change id
          const trackedAddAttrs = trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Add, { changeId: trackedChangesState.getParentChangeId(tr.doc.resolve(newBlockStart), ChangeType.Add) });

          // Find the block(s) containing the selection
          const fromParentBlock = getClosestBlock(selection, selection.$from);
          const toParentBlock: FoundNodeInfo = getClosestBlock(selection, selection.$to);
          const toParentBlockStart$ = state.doc.resolve(toParentBlock.pos + 1);

          const selectionToEndOfBlockSlice = state.doc.slice(selection.from, toParentBlockStart$.after());
          const afterSelectionToEndOfBlockSlice = state.doc.slice(selection.to, toParentBlockStart$.after());

          const isSelectionAtStartOfBlock = selection.from - selectionToEndOfBlockSlice.openStart === fromParentBlock.pos || fromParentBlock.node.isAtom;
          const isSelectionAtEndOfBlock = selection.to + afterSelectionToEndOfBlockSlice.openStart === toParentBlockStart$.after();

          // If the selection is at the start of the block, then we need to insert the deleted content after the new block
          if (isSelectionAtStartOfBlock) {
            // If there was a selection that got deleted OR if the parent block was empty (meaning it was completely replaced by the new block)
            if (!selection.empty || toParentBlock.node.content.size === 0) {
              // Insert the deleted selection after the new block
              const posAfterBlock = newBlockStart + newBlockNode.nodeSize;
              const deletedSlice = copySliceWithChanges(trackedChangesState.changeMarkType, state.doc.slice(fromParentBlock.pos, isSelectionAtEndOfBlock ? toParentBlockStart$.after() : selection.to), trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Remove));
              if (!isPassthroughSlice(deletedSlice)) {
                tr.replaceRange(posAfterBlock, posAfterBlock + deletedSlice.openEnd, deletedSlice);
              }
            }
          }
          // Else this is like a split so we need to insert the deleted split content before the new block and the added split content after the new block
          else {
            // Insert the deleted selection before the new block
            const deletedSlice = copySliceWithChanges(trackedChangesState.changeMarkType, selectionToEndOfBlockSlice, trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Remove));
            tr.replaceRange(selection.from, selection.from + 1, deletedSlice);

            // Build the slice that contains the newly added split node
            // The entire slice is new so it should all have the tracked changes. So use a new slice with the same content that isn't open on either side
            let addedSliceAfterBlock = copySliceWithChanges(trackedChangesState.changeMarkType, new Slice(afterSelectionToEndOfBlockSlice.content, 0, 0), trackedAddAttrs);
            // But when inserting the slice back in it needs the original open start/end to be inserted correctly
            addedSliceAfterBlock = new Slice(addedSliceAfterBlock.content, afterSelectionToEndOfBlockSlice.openStart, afterSelectionToEndOfBlockSlice.openEnd);
            // If the selection isn't at the end of the block
            if (!isSelectionAtEndOfBlock) {
              // Insert the added content just after the new block
              const nodeEnd = getNodeEnd(tr, newBlockNode);
              tr.replaceWith(nodeEnd, nodeEnd + afterSelectionToEndOfBlockSlice.content.size, isEmptySlice(addedSliceAfterBlock) ? Fragment.empty : addedSliceAfterBlock.content);
            }
          }

          // Now that the doc is fully updated we can update the tracked attrs on the new block node.
          // We wait until the end because updating the attrs will change the node in the document and we won't be able to get its position with getNodeStart/getNodeEnd anymore
          // Call getNodeStart because the position of the new block might have changed from the transformations above
          updateNodeAttrs(tr, newBlockNode, getNodeStart(tr, newBlockNode), trackedAddAttrs);
        }
      }

      // Mark that the changes were tracked
      if (trackedChangesState?.enabled) {
        tr.setMeta(TrackedChangesTrackedMetaKey, true);
      }
      dispatch(tr.scrollIntoView());
    }

    return true;
  }
}
